import { V2_ROUTES } from "../state/useV2Navigation";

const DEEPLINK_ENTITY_MAX_AGE_MS = 2 * 60 * 1000;

const TAB_TO_ROUTE = {
  dashboard: V2_ROUTES.home,
  home: V2_ROUTES.home,
  "system-input": V2_ROUTES.home,
  "unified-editor": V2_ROUTES.explorer,
  "items-needing-review": V2_ROUTES.insightsInbox,
  "insights-inbox": V2_ROUTES.insightsInbox,
  "reports-dashboard": V2_ROUTES.reports,
  reports: V2_ROUTES.reports,
  "graph-visualization": V2_ROUTES.explorer,
  explorer: V2_ROUTES.explorer,
  "ai-agent": V2_ROUTES.home,
  admin: V2_ROUTES.admin,
  "entity-configuration": V2_ROUTES.admin,
};

function readDeeplinkEntityId() {
  if (typeof window === "undefined") return null;
  try {
    const p = new URLSearchParams(window.location.search);
    const fromUrl = p.get("entityId") || p.get("editEntity");
    if (fromUrl) return String(fromUrl).trim() || null;
    const raw = localStorage.getItem("app_deeplink_entity");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed?.id &&
      typeof parsed.ts === "number" &&
      Date.now() - parsed.ts < DEEPLINK_ENTITY_MAX_AGE_MS
    ) {
      return String(parsed.id).trim() || null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Auth flows from email links (unauthenticated). */
export function readAuthFlowFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const p = new URLSearchParams(window.location.search);
    const email = String(p.get("email") || "").trim();
    const token = String(p.get("token") || "").trim();
    if (!email || !token) return null;
    const type = String(p.get("type") || "").trim().toLowerCase();
    if (type === "confirm") {
      return { type: "email-verify", email, token };
    }
    if (type === "magic") {
      return { type: "magic-link", email, token };
    }
    if (type === "invite") {
      return { type: "reset-password", email, token, isInvitation: true };
    }
    return { type: "reset-password", email, token, isInvitation: false };
  } catch {
    return null;
  }
}

/**
 * One-shot startup navigation from URL (legacy ?tab= and entity deeplinks).
 * @returns {{ route?: string, explorerProps?: object } | null}
 */
export function readStartupNavigation() {
  if (typeof window === "undefined") return null;

  const auth = readAuthFlowFromUrl();
  if (auth) return null;

  const p = new URLSearchParams(window.location.search);
  const tab = String(p.get("tab") || "").trim();
  const createEntity = p.get("createEntity");
  const entityId =
    String(p.get("entityId") || p.get("editEntity") || "").trim() ||
    readDeeplinkEntityId() ||
    String(p.get("graphEntityId") || "").trim();

  let route = tab ? TAB_TO_ROUTE[tab] : null;
  if (!route && (entityId || createEntity)) {
    route = V2_ROUTES.explorer;
  }

  const explorerProps = {};
  if (entityId) explorerProps.initialEntityId = entityId;
  if (createEntity) explorerProps.initialOpenCreate = true;

  if (!route && !Object.keys(explorerProps).length) {
    return null;
  }

  return {
    route: route || V2_ROUTES.home,
    explorerProps: Object.keys(explorerProps).length ? explorerProps : undefined,
  };
}

export function cleanStartupUrl() {
  if (typeof window === "undefined") return;
  try {
    const cleaned = new URL(window.location.href);
    [
      "tab",
      "editEntity",
      "createEntity",
      "graphEntityId",
      "graphEntityName",
      "entityId",
      "ui",
      "email",
      "token",
      "type",
      "oidc_token",
      "oidc_code",
      "oidc_error",
    ].forEach((k) => cleaned.searchParams.delete(k));
    window.history.replaceState(
      {},
      document.title,
      `${cleaned.pathname}${cleaned.search}${cleaned.hash}`
    );
    localStorage.removeItem("app_deeplink_entity");
  } catch {
    /* ignore */
  }
}

export function cleanAuthUrlParams() {
  if (typeof window === "undefined") return;
  try {
    const cleaned = new URL(window.location.href);
    ["email", "token", "type", "oidc_token", "oidc_code", "oidc_error"].forEach((k) =>
      cleaned.searchParams.delete(k)
    );
    window.history.replaceState(
      {},
      document.title,
      `${cleaned.pathname}${cleaned.search}${cleaned.hash}`
    );
  } catch {
    /* ignore */
  }
}
